"use client";

import {
  Container,
  Title,
  Text,
  Button,
  Stack,
  Box,
  Card,
  Group,
  Badge,
  SimpleGrid,
  ThemeIcon,
  List,
  Alert,
  Divider,
  ColorSwatch,
  Paper,
  Grid,
  Table,
  Image,
} from "@mantine/core";
import {
  IconPalette,
  IconClock,
  IconCheck,
  IconArrowRight,
  IconPhoto,
  IconShirt,
  IconSparkles,
  IconUsers,
  IconStar,
  IconEye,
  IconHeart,
  IconUpload,
  IconShield,
  IconDownload,
  IconDroplet,
  IconRefresh,
  IconColorSwatch,
  IconFlower,
  IconSun,
  IconLeaf,
  IconSnowflake,
  IconDropletFilled,
  IconSunrise,
  IconTie,
  IconShirtFilled,
  IconCamera,
  IconFileDownload,
  IconShare,
} from "@tabler/icons-react";
import Link from "next/link";
import { PRICING } from "@/constants/pricing";
import UnifiedUpsell from "@/components/UnifiedUpsell";
import { trackCtaClick } from "@/lib/analytics";

export default function CoolVsWarmUndertoneTestsContent() {
  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        {/* Hero Section */}
        <Box ta="center">
          <Title order={1} size="h1" mb="md">
            Cool vs Warm Undertone Tests
          </Title>
          <Text size="lg" c="dimmed" maw={600} mx="auto" mb="xl">
            Take our cool vs warm undertone tests to determine your skin undertone. Learn the difference between cool
            and warm undertones and find your perfect colors.
          </Text>

          {/* Undertone Testing Guide Image */}
          <Box mb="xl">
            <Image
              src="/images/generated/undertone-testing-guide.jpg"
              alt="Skin Undertone Testing Guide and Methods"
              width={800}
              height={600}
              radius="md"
              style={{ maxWidth: "100%", height: "auto" }}
            />
          </Box>
        </Box>

        {/* Undertone Tests */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md">
            How to Test Your Undertone
          </Title>
          <Text mb="lg">
            There are several ways to determine if you have cool or warm undertones. Try these tests to find out:
          </Text>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
            <Box>
              <Title order={3} size="h4" mb="md" c="blue">
                Cool Undertone Signs
              </Title>
              <List size="sm" spacing="xs">
                <List.Item>Veins appear blue or purple</List.Item>
                <List.Item>Silver jewelry looks better</List.Item>
                <List.Item>Cool colors flatter you</List.Item>
                <List.Item>Pink or rosy undertones</List.Item>
              </List>
            </Box>
            <Box>
              <Title order={3} size="h4" mb="md" c="orange">
                Warm Undertone Signs
              </Title>
              <List size="sm" spacing="xs">
                <List.Item>Veins appear green or olive</List.Item>
                <List.Item>Gold jewelry looks better</List.Item>
                <List.Item>Warm colors flatter you</List.Item>
                <List.Item>Yellow or golden undertones</List.Item>
              </List>
            </Box>
          </SimpleGrid>
        </Card>

        {/* Detailed Tests */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md">
            Detailed Undertone Tests
          </Title>
          <Text mb="lg">
            Try these comprehensive tests to determine your skin undertone. For best results, test in natural lighting
            without makeup.
          </Text>
          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
            <Box>
              <ThemeIcon size={60} radius="xl" color="blue" variant="light" mb="md">
                <IconEye size={30} />
              </ThemeIcon>
              <Title order={3} size="h5" mb="sm">
                Vein Test
              </Title>
              <Text size="sm" c="dimmed" mb="md">
                Look at the veins on your wrist in natural light.
              </Text>
              <List size="sm" spacing="xs">
                <List.Item>Blue/purple veins = Cool undertone</List.Item>
                <List.Item>Green veins = Warm undertone</List.Item>
                <List.Item>Both or unclear = Neutral undertone</List.Item>
              </List>
            </Box>
            <Box>
              <ThemeIcon size={60} radius="xl" color="yellow" variant="light" mb="md">
                <IconStar size={30} />
              </ThemeIcon>
              <Title order={3} size="h5" mb="sm">
                Jewelry Test
              </Title>
              <Text size="sm" c="dimmed" mb="md">
                Try on gold and silver jewelry to see which looks better.
              </Text>
              <List size="sm" spacing="xs">
                <List.Item>Gold looks better = Warm undertone</List.Item>
                <List.Item>Silver looks better = Cool undertone</List.Item>
                <List.Item>Both look good = Neutral undertone</List.Item>
              </List>
            </Box>
            <Box>
              <ThemeIcon size={60} radius="xl" color="green" variant="light" mb="md">
                <IconPalette size={30} />
              </ThemeIcon>
              <Title order={3} size="h5" mb="sm">
                White Paper Test
              </Title>
              <Text size="sm" c="dimmed" mb="md">
                Hold a white piece of paper next to your face in natural light.
              </Text>
              <List size="sm" spacing="xs">
                <List.Item>Skin looks pink/blue = Cool undertone</List.Item>
                <List.Item>Skin looks yellow/peachy = Warm undertone</List.Item>
                <List.Item>No obvious tint = Neutral undertone</List.Item>
              </List>
            </Box>
          </SimpleGrid>
        </Card>

        {/* Bridge to virtual draping */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md">
            See It On Your Own Face
          </Title>
          <Text mb="lg">
            Upload a selfie and our free virtual draping tool wraps you in gold vs silver, cream vs white, and
            warm vs cool red drapes — the same test analysts do in person. Free, no signup.
          </Text>
          <Button
            component={Link}
            href="/virtual-color-draping?src=bridge_cvw"
            color="violet"
            rightSection={<IconArrowRight size={20} />}
            onClick={() => trackCtaClick("bridge_cvw_vd")}
          >
            Drape Yourself Free
          </Button>
        </Card>

        {/* Color Test */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md">
            Color Test
          </Title>
          <Text mb="lg">
            Try on different colored clothing or hold colored fabrics near your face to see which colors make you look
            more vibrant and healthy.
          </Text>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Box>
              <Title order={3} size="h5" mb="sm" c="blue">
                Cool Colors Test
              </Title>
              <Text size="sm" c="dimmed" mb="md">
                Try these cool colors and see if they make you look more vibrant:
              </Text>
              <Group gap="xs" mb="md">
                <ColorSwatch color="#4169E1" size={30} />
                <ColorSwatch color="#8A2BE2" size={30} />
                <ColorSwatch color="#20B2AA" size={30} />
                <ColorSwatch color="#708090" size={30} />
                <ColorSwatch color="#FF69B4" size={30} />
              </Group>
              <List size="sm" spacing="xs">
                <List.Item>Royal blue, navy, cool gray</List.Item>
                <List.Item>Purple, lavender, cool pink</List.Item>
                <List.Item>Teal, mint green, cool green</List.Item>
                <List.Item>If these look good, you likely have cool undertones</List.Item>
              </List>
            </Box>
            <Box>
              <Title order={3} size="h5" mb="sm" c="orange">
                Warm Colors Test
              </Title>
              <Text size="sm" c="dimmed" mb="md">
                Try these warm colors and see if they make you look more vibrant:
              </Text>
              <Group gap="xs" mb="md">
                <ColorSwatch color="#FF8C00" size={30} />
                <ColorSwatch color="#FFD700" size={30} />
                <ColorSwatch color="#CD853F" size={30} />
                <ColorSwatch color="#8B4513" size={30} />
                <ColorSwatch color="#32CD32" size={30} />
              </Group>
              <List size="sm" spacing="xs">
                <List.Item>Orange, gold, warm brown</List.Item>
                <List.Item>Peach, coral, warm pink</List.Item>
                <List.Item>Olive green, warm green</List.Item>
                <List.Item>If these look good, you likely have warm undertones</List.Item>
              </List>
            </Box>
          </SimpleGrid>
        </Card>

        {/* Sun Reaction Test */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md">
            Sun Reaction Test
          </Title>
          <Text mb="lg">How your skin reacts to sun exposure can also indicate your undertone:</Text>
          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
            <Box>
              <Title order={3} size="h5" mb="sm" c="blue">
                Cool Undertone
              </Title>
              <Text size="sm" c="dimmed" mb="md">
                Typically burns easily and rarely tans.
              </Text>
              <List size="sm" spacing="xs">
                <List.Item>Burns easily in the sun</List.Item>
                <List.Item>Rarely tans or tans very little</List.Item>
                <List.Item>May have freckles</List.Item>
                <List.Item>Skin may turn pink or red</List.Item>
              </List>
            </Box>
            <Box>
              <Title order={3} size="h5" mb="sm" c="orange">
                Warm Undertone
              </Title>
              <Text size="sm" c="dimmed" mb="md">
                Typically tans easily and rarely burns.
              </Text>
              <List size="sm" spacing="xs">
                <List.Item>Tans easily in the sun</List.Item>
                <List.Item>Rarely burns</List.Item>
                <List.Item>Gets golden or olive tan</List.Item>
                <List.Item>Skin may turn golden brown</List.Item>
              </List>
            </Box>
            <Box>
              <Title order={3} size="h5" mb="sm" c="gray">
                Neutral Undertone
              </Title>
              <Text size="sm" c="dimmed" mb="md">
                May burn first, then tan.
              </Text>
              <List size="sm" spacing="xs">
                <List.Item>Burns first, then tans</List.Item>
                <List.Item>Moderate sun sensitivity</List.Item>
                <List.Item>May have mixed reactions</List.Item>
                <List.Item>Skin may turn pink then brown</List.Item>
              </List>
            </Box>
          </SimpleGrid>
        </Card>

        {/* Common Mistakes */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md">
            Common Mistakes to Avoid
          </Title>
          <Text mb="lg">Avoid these common mistakes when testing your undertone:</Text>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Box>
              <Title order={3} size="h5" mb="sm" c="red">
                ❌ What Not to Do
              </Title>
              <List size="sm" spacing="xs">
                <List.Item>Testing in artificial lighting</List.Item>
                <List.Item>Wearing makeup or self-tanner</List.Item>
                <List.Item>Testing on tanned skin</List.Item>
                <List.Item>Using colored clothing that affects the test</List.Item>
                <List.Item>Testing in poor lighting or shadows</List.Item>
                <List.Item>Rushing through the tests</List.Item>
              </List>
            </Box>
            <Box>
              <Title order={3} size="h5" mb="sm" c="green">
                ✅ Best Practices
              </Title>
              <List size="sm" spacing="xs">
                <List.Item>Test in natural daylight near a window</List.Item>
                <List.Item>Remove all makeup and skincare products</List.Item>
                <List.Item>Test on clean, bare skin</List.Item>
                <List.Item>Wear neutral-colored clothing</List.Item>
                <List.Item>Take your time with each test</List.Item>
                <List.Item>Test multiple areas of your body</List.Item>
              </List>
            </Box>
          </SimpleGrid>
        </Card>

        {/* FAQ */}
        <Card withBorder p="xl" radius="md">
          <Title order={2} mb="md">
            Frequently Asked Questions
          </Title>
          <Stack gap="md">
            <Box>
              <Title order={3} size="h5" mb="sm">
                What if I'm between cool and warm?
              </Title>
              <Text size="sm" c="dimmed">
                You likely have neutral undertones, which means you can wear both cool and warm colors. You might be a
                neutral season or have a preference for one temperature family.
              </Text>
            </Box>
            <Divider />
            <Box>
              <Title order={3} size="h5" mb="sm">
                Does my tan affect my undertone?
              </Title>
              <Text size="sm" c="dimmed">
                No, a tan doesn't change your undertone. Look at areas that don't tan (like your inner wrist) or
                consider your natural coloring before any sun exposure.
              </Text>
            </Box>
            <Divider />
            <Box>
              <Title order={3} size="h5" mb="sm">
                What if the tests give different results?
              </Title>
              <Text size="sm" c="dimmed">
                Try multiple tests and see which result is most consistent. If you're still unsure, our AI analysis can
                provide a definitive answer by analyzing your photo.
              </Text>
            </Box>
            <Divider />
            <Box>
              <Title order={3} size="h5" mb="sm">
                Can I change my undertone?
              </Title>
              <Text size="sm" c="dimmed">
                No, your undertone is determined by your genetics and doesn't change. However, your overtone (surface
                color) can change with sun exposure, makeup, or other factors.
              </Text>
            </Box>
          </Stack>
        </Card>

        {/* CTA */}
        <Box ta="center" py="xl">
          <Title order={2} mb="md">
            Ready to Find Your Undertone?
          </Title>
          <Text size="lg" c="dimmed" mb="xl">
            Take our comprehensive color analysis quiz to determine your exact undertone and color season.
          </Text>
          <Group justify="center" gap="md">
            <Button
              size="xl"
              component={Link}
              href="/color-analysis-quiz"
              color="violet"
              rightSection={<IconPalette size={20} />}
            >
              Take Color Quiz
            </Button>
            <Button
              size="xl"
              variant="outline"
              component={Link}
              href="/ai-color-analysis"
              color="violet"
              rightSection={<IconPhoto size={20} />}
            >
              Get Photo Analysis
            </Button>
          </Group>
        </Box>
      </Stack>
    </Container>
  );
}
